// Tax Savings Packet — "Build packet" (Philip 2026-10-30, docs/tax-savings-packet-plan.md).
//   Step 1 (documents): the top sheet plus every document filed for the property and the ticked parcels in the value
//     year (a parcel with no bill that year: its prior year's, flagged), grouped in packet order with the starting
//     ticks of src/reports/taxSavingsPacket.js. Any of them can be ticked or unticked; click a name to view it.
//   Step 2 (pages): "Create packet" merges the ticked ones into one PDF (images become pages); every page as a
//     thumbnail (src/components/PageOrganizer.js) — drag or ← → to reorder, × to delete, Undo. Then Print, Save PDF, Save to Documents (Billing → Tax Savings
//     Packet → value year), logged in the activity log with the parcels and documents included.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../supabase';
import { fetchAll, fetchByParcelIds } from '../reports/pagination';
import { packetItems, PACKET_SECTIONS, fileKind } from '../reports/taxSavingsPacket';
import { fileUrl } from '../utils/fileUrl';
import { pdfThumbnails } from '../utils/pdfThumbs';
import { loadDocVocab } from '../utils/docVocab';
import { buildFilingUpdates } from '../constants/docFiling';
import { uploadFile } from '../utils/uploadFile';
import { TAX_BILL_STATUS } from '../constants/statuses';
import PageOrganizer from './PageOrganizer';

const FONT = "'Public Sans',system-ui,sans-serif";
const TYPE_KEY = 'tax_savings_packet';
export const SAVE_ACTION = 'tax_savings_packet_saved';
const TOP = 'top-sheet';
const DOC_COLS = 'id,file_name,folder_key,type_key,year_subfolder,parcel_id,property_id,appeal_level,storage_path,filed_at,created_at';
const safeName = (s) => String(s || '').replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim();

// topSheet: () => Promise<Blob> — the printed top sheet (no footnotes or marks).
export default function TaxSavingsPacketModal({ darkMode, staffUser, property, report, rows, level, typeLabel, topSheet, onClose }) {
  const c = darkMode
    ? { bg: '#1f2638', card: 'rgba(47,56,84,0.6)', border: '#374060', text: '#e8eaf2', muted: '#a8b0cc', soft: 'rgba(26,34,56,0.6)' }
    : { bg: '#ffffff', card: '#ffffff', border: 'var(--line)', text: 'var(--ink)', muted: 'var(--slate)', soft: 'var(--line-soft)' };
  const btn = { padding: '6px 12px', background: 'var(--red)', border: '1px solid var(--red)', borderRadius: '7px', color: '#fff', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: FONT };
  const btnGhost = { ...btn, background: 'transparent', border: `1px solid ${c.border}`, color: c.text };
  const off = (s) => ({ ...s, opacity: 0.5, cursor: 'default' });

  const year = report.year;
  const [items, setItems] = useState(null);
  const [checked, setChecked] = useState(new Set());
  const [viewing, setViewing] = useState(null);     // { id, url, kind, name }
  const [step, setStep] = useState('docs');
  const [merged, setMerged] = useState(null);       // { bytes, pages: [{ label, page }], thumbs: [dataURL] }
  const [order, setOrder] = useState([]);           // page indices into merged, in packet order
  const [history, setHistory] = useState([]);       // earlier orders, for Undo
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const printRef = useRef(null);

  // The documents: the property's (any year folder this year or last) and the ticked parcels' (filed anywhere — a
  // pro-rata parcel's documents sit on its own property), plus which bills Wigley's figures were scanned from.
  useEffect(() => {
    (async () => {
      try {
        const years = [year, String(Number(year) - 1)];
        const parcelIds = [...new Set(rows.map((r) => r.parcel_id))];
        const [onProperty, onParcels, bills] = await Promise.all([
          fetchAll('file_exchange', DOC_COLS, (q) => q.eq('property_id', property.id).in('year_subfolder', years)),
          fetchByParcelIds('file_exchange', DOC_COLS, parcelIds, (q) => q.in('year_subfolder', years)),
          fetchByParcelIds('tax_bills', 'parcel_id,tax_year,status,superseded_at,file_exchange_id', parcelIds, (q) => q.in('tax_year', years)),
        ]);
        const docs = [...new Map([...onProperty, ...onParcels].map((d) => [d.id, d])).values()];
        const billDocIds = new Set(bills.filter((b) => !b.superseded_at && b.status !== TAX_BILL_STATUS.REJECTED && b.file_exchange_id).map((b) => b.file_exchange_id));
        const list = packetItems({ docs, year, billDocIds, parcels: rows.map((r) => ({ parcel_id: r.parcel_id, parcel: r.parcel, levelKey: r.levelKey })) });
        setItems(list);
        setChecked(new Set(list.filter((i) => i.checked).map((i) => i.id)));
      } catch (e) { setErr(`The documents could not be loaded: ${e.message}`); }
    })();
  }, []);

  const toggle = (id) => setChecked((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const view = async (it) => {
    setErr('');
    try {
      if (it === TOP) {
        const blob = await topSheet();
        setViewing({ id: TOP, url: URL.createObjectURL(blob), kind: 'pdf', name: 'Top sheet' });
        return;
      }
      const url = await fileUrl(it.doc.storage_path, 'preview');
      setViewing({ id: it.id, url, kind: fileKind(it.doc.file_name), name: it.doc.file_name });
    } catch (e) { setErr(`"${it === TOP ? 'Top sheet' : it.doc.file_name}" could not be opened: ${e.message}`); }
  };

  const chosen = useMemo(() => (items || []).filter((i) => checked.has(i.id)), [items, checked]);

  const createPacket = async () => {
    setBusy('Creating the packet…'); setErr(''); setMsg('');
    try {
      const PDFLib = await import('pdf-lib');
      const { mergePacket } = await import('../reports/pdf/packetPdf');
      const parts = [{ label: 'Top sheet', kind: 'pdf', bytes: await (await topSheet()).arrayBuffer() }];
      for (const it of chosen) {
        setBusy(`Fetching ${it.doc.file_name}…`);
        const resp = await fetch(await fileUrl(it.doc.storage_path, 'download'));
        if (!resp.ok) throw new Error(`"${it.doc.file_name}" could not be fetched (HTTP ${resp.status}).`);
        const bytes = await resp.arrayBuffer();
        if (!bytes.byteLength) throw new Error(`"${it.doc.file_name}" came back empty (0 bytes).`);
        parts.push({ label: it.doc.file_name, kind: fileKind(it.doc.file_name), bytes });
      }
      setBusy('Merging…');
      const m = await mergePacket(PDFLib, parts);
      setBusy('Drawing the pages…');
      const thumbs = await pdfThumbnails(m.bytes);
      setMerged({ ...m, thumbs });
      setOrder(m.pages.map((_, i) => i));
      setHistory([]);
      setStep('pages');
    } catch (e) { setErr(e.message); } finally { setBusy(''); }
  };

  const change = (next) => { setHistory((h) => [...h, order]); setOrder(next); };
  const undo = () => { setHistory((h) => { if (!h.length) return h; setOrder(h[h.length - 1]); return h.slice(0, -1); }); };

  const finalBlob = async () => {
    const PDFLib = await import('pdf-lib');
    const { reorderPdf } = await import('../reports/pdf/packetPdf');
    return new Blob([await reorderPdf(PDFLib, merged.bytes, order)], { type: 'application/pdf' });
  };
  const fileName = `${safeName(`${year} Tax Savings Packet - ${property.name} - ${report.levelLabel}`)}.pdf`;

  const doPrint = async () => {
    setErr('');
    try {
      const url = URL.createObjectURL(await finalBlob());
      const frame = printRef.current;
      frame.onload = () => { try { frame.contentWindow.print(); } catch (e) { setErr(`Print failed: ${e.message}`); } };
      frame.src = url;
    } catch (e) { setErr(`Print failed: ${e.message}`); }
  };
  const doDownload = async () => {
    setErr('');
    try {
      const url = URL.createObjectURL(await finalBlob());
      const a = document.createElement('a');
      a.href = url; a.download = fileName;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { setErr(`Save PDF failed: ${e.message}`); }
  };

  // Billing → Tax Savings Packet → value year, on the property (Philip 2026-10-30). Upload first: no row for bytes not in storage.
  const doSave = async () => {
    setBusy('Saving to Documents…'); setErr(''); setMsg('');
    try {
      const by = staffUser?.name || 'NTRG Staff';
      const vocab = await loadDocVocab();
      const { updates, folder, folderName } = buildFilingUpdates({
        vocab, docType: TYPE_KEY, clientId: property.client_id, propertyId: property.id, parcelId: null, year,
        viewableClient: false, viewableConsultant: false, existingPropertyId: property.id, staffName: by,
      });
      const blob = await finalBlob();
      const storagePath = `${property.client_id}/mpid-${property.master_property_id}/${folder}/${year}/${Date.now()}_${fileName}`;
      const file = new File([blob], fileName, { type: 'application/pdf' });
      const { error: ue } = await uploadFile(storagePath, file);
      if (ue) throw new Error(`The file failed to upload — nothing was saved. ${ue.message}`);
      const { data, error } = await supabase.from('file_exchange').insert({
        ...updates, master_property_id: String(property.master_property_id || ''),
        uploaded_by_name: by, direction: 'ntrg_internal', file_name: fileName, file_size_bytes: file.size, storage_path: storagePath,
      }).select('id').single();
      if (error || !data?.id) throw new Error(`Saving failed — ${error ? error.message : 'no row was created'}`);
      const { error: logErr } = await supabase.from('activity_log').insert({
        staff_user: by, action: SAVE_ACTION, entity_type: 'file_exchange', entity_id: data.id,
        entity_name: `Tax Savings Packet · ${property.name} · ${year}`, property_name: property.name || null,
        details: {
          property_id: property.id, client_id: property.client_id, tax_year: year, level, level_label: report.levelLabel, property_type: typeLabel,
          parcels_included: report.rows.map((r) => r.parcel), parcels: `${report.of.included} of ${report.of.total}`,
          savings: typeof report.total.savings === 'number' ? Math.round(report.total.savings * 100) / 100 : report.total.savings,
          documents: chosen.map((i) => ({ id: i.id, name: i.doc.file_name, section: i.section, parcel: i.parcelLabel, prior_year: i.note.includes('prior year') })),
          pages: order.length, file_name: fileName,
        },
      });
      setMsg(`Saved to ${property.name} → Documents → ${folderName} → ${year}.${logErr ? ` The activity-log entry was not saved (${logErr.message}).` : ''}`);
    } catch (e) { setErr(e.message); } finally { setBusy(''); }
  };

  const section = (key) => (items || []).filter((i) => i.section === key);
  const row = (it) => (
    <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 6px', borderRadius: '6px', background: viewing?.id === it.id ? c.soft : 'transparent' }}>
      <input type="checkbox" checked={checked.has(it.id)} disabled={!it.mergeable} onChange={() => toggle(it.id)} />
      <button type="button" onClick={() => view(it)} style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none', padding: 0, color: c.text, fontSize: '12px', cursor: 'pointer', fontFamily: FONT }}>
        {it.doc.file_name}
      </button>
      <span style={{ fontSize: '11px', color: c.muted, whiteSpace: 'nowrap' }}>{it.parcelLabel}</span>
      {it.note && <span style={{ fontSize: '10px', fontWeight: 700, color: it.note.includes('prior') ? '#b45309' : c.muted, whiteSpace: 'nowrap' }}>{it.note}</span>}
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,12,20,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT }}>
      <div style={{ background: c.bg, color: c.text, borderRadius: '14px', width: 'min(1280px, 96vw)', height: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 18px', borderBottom: `1px solid ${c.border}` }}>
          <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '18px', fontWeight: 700 }}>Tax Savings Packet</div>
          <div style={{ fontSize: '12px', color: c.muted }}>{property.name} · {year} · {report.levelLabel} · {typeLabel} · {report.of.included} parcel{report.of.included === 1 ? '' : 's'}</div>
          <div style={{ flex: 1 }} />
          {step === 'docs'
            ? <button style={busy || !items ? off(btn) : btn} disabled={!!busy || !items} onClick={createPacket}>Create packet ({chosen.length + 1})</button>
            : <>
              <button style={btnGhost} onClick={() => { setStep('docs'); setMsg(''); }}>← Documents</button>
              <button style={history.length ? btnGhost : off(btnGhost)} disabled={!history.length} onClick={undo}>Undo</button>
              <button style={order.length ? btnGhost : off(btnGhost)} disabled={!order.length} onClick={doPrint}>Print</button>
              <button style={order.length ? btnGhost : off(btnGhost)} disabled={!order.length} onClick={doDownload}>Save PDF</button>
              <button style={order.length && !busy ? btn : off(btn)} disabled={!order.length || !!busy} onClick={doSave}>Save to Documents</button>
            </>}
          <button style={btnGhost} onClick={onClose}>Close</button>
        </div>
        {(err || msg || busy) && (
          <div style={{ padding: '8px 18px', fontSize: '12px', color: err ? 'var(--red)' : msg ? (darkMode ? '#bdebd0' : '#17663a') : c.muted }}>{err || msg || busy}</div>
        )}

        {step === 'docs' ? (
          <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
            <div style={{ width: '46%', overflowY: 'auto', padding: '12px 16px', borderRight: `1px solid ${c.border}` }}>
              {!items && !err && <div style={{ fontSize: '12px', color: c.muted }}>Loading documents…</div>}
              {items && PACKET_SECTIONS.map((s, n) => (
                <div key={s.key} style={{ marginBottom: '14px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>{n + 1}. {s.label}</div>
                  {s.key === 'top'
                    ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 6px', borderRadius: '6px', background: viewing?.id === TOP ? c.soft : 'transparent' }}>
                        <input type="checkbox" checked disabled />
                        <button type="button" onClick={() => view(TOP)} style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none', padding: 0, color: c.text, fontSize: '12px', cursor: 'pointer', fontFamily: FONT }}>
                          {report.title} — {property.name}
                        </button>
                        <span style={{ fontSize: '10px', fontWeight: 700, color: c.muted }}>always included</span>
                      </div>
                    )
                    : section(s.key).length ? section(s.key).map(row) : <div style={{ fontSize: '12px', color: c.muted, fontStyle: 'italic', padding: '3px 6px' }}>Nothing filed for {year}.</div>}
                </div>
              ))}
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, background: c.soft }}>
              {viewing
                ? viewing.kind === 'pdf'
                  ? <iframe title={viewing.name} src={viewing.url} style={{ flex: 1, border: 'none', background: '#fff' }} />
                  : viewing.kind
                    ? <div style={{ flex: 1, overflow: 'auto', textAlign: 'center', padding: '12px' }}><img alt={viewing.name} src={viewing.url} style={{ maxWidth: '100%' }} /></div>
                    : <div style={{ margin: 'auto', fontSize: '12px', color: c.muted }}>This file can’t be previewed or merged. <a href={viewing.url} target="_blank" rel="noreferrer">Open it</a></div>
                : <div style={{ margin: 'auto', fontSize: '12px', color: c.muted }}>Click a document to view it.</div>}
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>
            <div style={{ fontSize: '12px', color: c.muted, marginBottom: '10px' }}>{order.length} page{order.length === 1 ? '' : 's'} — drag a page (or use ← →) to move it, × to delete it.</div>
            <PageOrganizer order={order} thumbs={merged.thumbs} labels={merged.pages.map((p) => `${p.label} · p${p.page}`)} onChange={change} colors={c} />
          </div>
        )}
        <iframe ref={printRef} title="print" style={{ display: 'none' }} />
      </div>
    </div>
  );
}
